import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import ArrowRightGrey from '../../assets/arrow_right_grey.svg';
import BtArrowDown from '../../assets/bt_arrow_down_white.svg';
import BtArrowUp from '../../assets/bt_arrow_up_white.svg';
import FooterSectionImage1 from '../../assets/footer_section_image1.png';
import WebAwardLogo from '../../assets/web_award_logo.svg';
import theme from '../../common/color/theme';
import { HorizontalEqualLayout, HorizontalLayout, useResponsivePadding, VerticalLayout } from '../../common/common-layout';
import { TitleNormal, TitleNormalBold } from '../../common/common-text';
import BTBox from '../../common/components/BTBox';
import { fetchCopyright } from '../../common/firebase/FirestoreService';
import i18n from '../../common/i18n/i18n';
import { pxToRem } from '../../common/utils/ConvertUtils';
import { MULTIPLE_LANGUAGE } from '../../config';
import '../../css/bbpage.css';
import { useDeviceType } from '../../hooks/useDeviceType';
import Copyright from '../../model/Copyright';
import menuStore from '../../store/MenuStore';

const FooterSectionMobile = () => {
  const { horizontal: padding } = useResponsivePadding();
  const navigate = useNavigate();
  const { isDesktop } = useDeviceType();
  const { t } = useTranslation();
  const location = useLocation();
  const [copyright, setCopyright] = useState<Copyright | null>(null);
  const isEng = ['en', 'en-mobile'].includes(i18n.language);
  const isChina = ['zh', 'zh-mobile'].includes(i18n.language);

  function getCurrentLangName(lang: string): string {
    switch (lang) {
      case 'ko':
        return 'KOR';
      case 'ko-mobile':
        return 'KOR';
      case 'en':
        return 'ENG';
      case 'en-mobile':
        return 'ENG';
      case 'zh':
        return 'CHN';
      case 'zh-mobile':
        return 'CHN';
      default:
        return 'KOR';
    }
  }

  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [activeMenu, setActiveMenu] = useState<string[]>([]);
  const TITLE_BOTTOM_MARGIN = 30;
  const SUB_MENU_GAP = 20;

  const currentLangName = getCurrentLangName(i18n.language); // 기존에 쓰던 함수
  const languages = [
    { code: 'ko', name: '한국어' },
    { code: 'en', name: 'English' },
    { code: 'zh', name: '简体中文' },
  ];

  const handleSelect = (code: string) => {
    i18n.changeLanguage(code);
    setIsLangOpen(false);
  };

  // 바깥 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const copyRight = await fetchCopyright();
      setCopyright(copyRight);
    };

    fetchData();
  }, []);

  const getMargin = (ko: number, en: number, ja: number, zh: number): number => {
    switch (currentLang) {
      case 'en':
        return en;
      case 'ja':
        return ja;
      case 'zh':
        return zh;
      case 'ko':
      default:
        return ko;
    }
  };
  const currentLang = i18n.language;

  const getMenuName = () => {
    switch (location.pathname) {
      case '/introduce/vision':
        return t('경영비전');
      case '/introduce/ceo':
        return t('CEO 인사말');
      case '/introduce/history':
        return t('회사연혁');
      case '/introduce/qualification':
        return t('특허 및 인증');
      case '/introduce/organization':
        return t('조직도');
      case '/introduce/ci':
        return t('CI');
      case '/introduce/location':
        return t('찾아오시는 길');
      case '/business':
        return t('반도체');
      case '/business/battery':
        return t('이차전지');
      case '/business/dryroom':
        return t('클린룸/드라이룸');
      case '/business/teflon':
        return t('테프론코팅');
      case '/business/cryogenic':
        return t('극저온배관');
      case '/business/ac':
        return t('자동 제관사업');
      case '/promotion':
        return t('미디어센터');
      case '/promotion/news':
        return t('사내소식');
      case '/customer/inquiry':
        return t('문의하기');
      case '/customer/report':
        return t('BB열린소리');
      case '/recruit':
        return t('채용공고');
      case '/recruit/people':
        return t('인재상');
      case '/recruit/benefits':
        return t('복지제도');
      case '/recruit/bbpeople':
        return t('직무소개');
      default:
        return ''; // 혹은 '메뉴 없음'
    }
  };

  const handleMenuClick = (menu: string, href: string) => {
    var isMenuChanged = menuStore.currentSection !== href;
    toggleMenu(menu);

    if (!isMenuChanged) {
      setIsOpen(false);
      menuStore.setOpen(false);
      menuStore.hide();
      window.location.reload();
      return;
    }
    menuStore.setCurrentSection(href);

    menuStore.setBackgroundColor('transparent');
    if (href === '#section1') {
      menuStore.setDarkMode(false);
    } else {
      menuStore.setDarkMode(true);
    }

    if (href === '#section1') {
      navigate('/');
    } else if (href === '#section1-1') {
      navigate('/introduce/vision');
    } else if (href === '#section1-2') {
      navigate('/introduce/ceo');
    } else if (href === '#section1-3') {
      navigate('/introduce/history');
    } else if (href === '#section1-4') {
      navigate('/introduce/qualification');
    } else if (href === '#section1-5') {
      navigate('/introduce/organization');
    } else if (href === '#section1-6') {
      navigate('/introduce/ci');
    } else if (href === '#section1-7') {
      navigate('/introduce/location');
    } else if (href === '#section2') {
      navigate('/business');
    } else if (href === '#section2-1') {
      navigate('/business');
    } else if (href === '#section2-2') {
      navigate('/business/battery');
    } else if (href === '#section2-3') {
      navigate('/business/datacenter');
    } else if (href === '#section2-4') {
      navigate('/business/dryroom');
    } else if (href === '#section2-5') {
      navigate('/business/teflon');
    } else if (href === '#section2-6') {
      navigate('/business/cryogenic');
    } else if (href === '#section2-7') {
      navigate('/business/ac');
    } else if (href === '#section2-8') {
      navigate('/business/smartfab');
    } else if (href === '#section3') {
      navigate('/promotion');
    } else if (href === '#section3-1') {
      navigate('/promotion');
    } else if (href === '#section3-2') {
      navigate('/promotion/news');
    } else if (href === '#section4') {
      navigate('/customer/inquiry');
    } else if (href === '#section4-1') {
      navigate('/customer/inquiry');
    } else if (href === '#section4-2') {
      navigate('/customer/report');
    } else if (href === '#section5') {
      if (isEng || isChina) {
        navigate('/recruit/people');
      } else {
        navigate('/recruit');
      }
    } else if (href === '#section5-1') {
      if (isEng || isChina) {
        navigate('/recruit/people');
      } else {
        navigate('/recruit');
      }
    } else if (href === '#section5-2') {
      navigate('/recruit/people');
    } else if (href === '#section5-3') {
      navigate('/recruit/benefits');
    } else if (href === '#section5-4') {
      navigate('/recruit/bbpeople');
    }
  };

  const menuData = [
    {
      name: t('회사소개'),
      href: '#section1',
      marginRight: getMargin(isDesktop ? 52 : 52, 8, 12, 70),
      subs: [
        t('경영비전'),
        t('CEO 메시지'),
        t('회사연혁'),
        t('특허 및 인증'),
        t('조직도'),
        t('CI'),
        t('찾아오시는 길'),
      ],
    },
    {
      name: t('사업영역'),
      href: '#section2',
      marginRight: getMargin(isDesktop ? 28 : 30, 58, 8, 16),
      subs: [
        t('반도체'),
        t('이차전지'),
        t('데이터센터'),
        t('클린룸/드라이룸'),
        t('테프론코팅'),
        t('극저온배관'),
      ],
    },
    ...((!isEng && !isChina)
      ? [{
        name: t('홍보채널'),
        href: '#section3',
        marginRight: getMargin(isDesktop ? 77 : 76, 148, 110, 106),
        subs: [
          t('미디어센터'),
          t('사내소식'),
        ],
      }]
      : []
    ),
    ...((!isEng && !isChina)
      ? [{
        name: t('고객지원'),
        href: '#section4',
        marginRight: getMargin(isDesktop ? 97 : 100, 153, 160, 93),
        subs: [
          t('문의하기'),
          t('BB열린소리'),
        ],
      }]
      : []
    ),
    {
      name: t('채용'),
      href: '#section5',
      marginRight: getMargin(isDesktop ? 94 : 98, 37, 56, 98),
      subs: (isEng || isChina)
        ? [t('인재상')]
        : [
          t('채용공고'),
          t('인재상'),
          t('복지제도'),
          t('직무소개'),
        ],
    },
    ...(MULTIPLE_LANGUAGE
      ? [{
        name: currentLangName,
        href: '#section6',
        marginRight: getMargin(isDesktop ? 85 : 114, 85, 85, 85),
        subs: [
          { label: t('ENG'), lang: 'en' },
          { label: t('CHN'), lang: 'zh' },
          { label: t('KOR'), lang: 'ko' },
        ],
      }]
      : []),
  ];

  const toggleMenu = (menuName: string) => {
    setActiveMenu((prev) =>
      prev.includes(menuName)
        ? prev.filter((m) => m !== menuName) // 이미 열려있으면 닫기
        : [...prev, menuName]                // 아니면 추가
    );
  };

  return (
    <VerticalLayout
      style={{
        background: 'black',
        paddingLeft: padding,
        paddingRight: padding,
        paddingBottom: `${pxToRem(54)}`
      }}
    >
      <BTBox height={31} />
      <HorizontalLayout>
        <img src={FooterSectionImage1} style={{ width: `${pxToRem(78)}`, height: `${pxToRem(100)}`, marginRight: 'auto' }} onClick={() => {
          menuStore.setCurrentSection('#section1');
          navigate('/');
        }} />
        {/* <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
          <HorizontalLayout style={{ alignItems: 'center', cursor: 'pointer' }} onClick={() => setIsLangOpen(prev => !prev)}>
            <TitleNormal fontSize={14} color="white">{currentLangName}</TitleNormal>
            <BTBox width={5} />
            <img src={ArrowDown} />
          </HorizontalLayout>

          {isLangOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                backgroundColor: '#fff',
                color: '#000',
                borderRadius: 4,
                boxShadow: '0px 2px 10px rgba(0,0,0,0.2)',
                zIndex: 1000,
                minWidth: 100,
                padding: 8
              }}
            >
              {languages.map(lang => (
                <div
                  key={lang.code}
                  onClick={() => handleSelect(lang.code)}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    fontWeight: i18n.language === lang.code ? 'bold' : 'normal'
                  }}
                >
                  {lang.name}
                </div>
              ))}
            </div>
          )}
        </div> */}
      </HorizontalLayout>
      <BTBox height={31} />
      <TitleNormalBold fontSize={14} color='white'>{t(`${copyright?.companyName}`)}</TitleNormalBold>
      <BTBox height={13} />
      <TitleNormal fontSize={12} color='white'>{t(`${copyright?.address}`)}</TitleNormal>
      <BTBox height={8} />
      <HorizontalLayout wrapContents={true}>
        <TitleNormal fontSize={12} color='white'>
          {t('사업자등록번호')}
          &nbsp;
          {t(copyright?.corpRegNo ?? '')}
        </TitleNormal>
        <BTBox width={10} />
        <TitleNormal fontSize={12} color='white' style={{ paddingLeft: '5px', paddingRight: '5px', opacity: 0.2 }}> | </TitleNormal>
        <BTBox width={10} />
        <TitleNormal fontSize={12} color='white'>
          {t('대표자.')}
          &nbsp;
          {t(copyright?.representPersonName ?? '')}
        </TitleNormal>
        <BTBox width={4} />
      </HorizontalLayout>

      <BTBox height={32} />

      {/* ✅ 메뉴 영역 */}
      {menuData.map((menu, idx) => (
        <div key={menu.name} style={{ marginBottom: '16px', width: '100%' }}>
          <VerticalLayout style={{ width: '100%' }} onClick={() => toggleMenu(menu.name)}>
            <HorizontalLayout style={{ justifyContent: 'space-between', width: '100%' }}>
              <TitleNormalBold fontSize={14} style={{ color: 'white', marginRight: 'auto' }}>{menu.name}</TitleNormalBold>
              {activeMenu.includes(menu.name) ? <img src={BtArrowUp} style={{ marginRight: '10px' }} /> : <img src={BtArrowDown} style={{ marginRight: '10px' }} />}
            </HorizontalLayout>
          </VerticalLayout>

          {activeMenu.includes(menu.name) && (
            <div
              style={{
                marginTop: '16px',
                fontSize: '16px',
                color: '#444',
                textAlign: 'left',
              }}
            >
              {menu.subs.map((sub, index) => {
                const subHref = `${menu.href}-${index + 1}`;

                // 언어 선택 영역
                if (typeof sub === 'object' && 'lang' in sub) {
                  return (
                    <div key={index} style={{ marginBottom: '6px' }}>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          i18n.changeLanguage(sub.lang);
                        }}
                        style={{ fontSize: '13px', color: '#ffffff' }}
                      >
                        {sub.label}
                      </a>
                    </div>
                  );
                }

                return (
                  <div key={index} style={{ marginBottom: '6px' }}>
                    <a
                      href={subHref}
                      onClick={(e) => {
                        e.preventDefault();
                        handleMenuClick(menu.name, subHref);
                        setIsOpen(false);
                      }}
                      style={{ fontSize: '13px', color: sub === getMenuName() ? theme.colors.BIBI : '#ffffff', fontWeight: sub === getMenuName() ? 700 : 400 }}
                    >
                      {sub}
                    </a>
                  </div>
                );
              })}
            </div>
          )}
          <BTBox height={12} />
          <VerticalLayout>
            <div style={{ width: '100%', height: '1px', backgroundColor: 'white', opacity: 0.2 }} />
          </VerticalLayout>
        </div>
      ))}

      <BTBox height={12} />
      <HorizontalEqualLayout>
        <TitleNormal fontSize={14} color='white' onClick={() => navigate('/terms')}>{t('이용약관')}</TitleNormal>
        <TitleNormalBold fontSize={14} color='white' onClick={() => navigate('/terms/policy')}>{t('개인정보처리방침')}</TitleNormalBold>
      </HorizontalEqualLayout>
      <BTBox height={30} />
      <TitleNormalBold fontSize={13} color='white'>{t('CONTACT')}</TitleNormalBold>
      <BTBox height={8} />
      <HorizontalEqualLayout>
        <a href="mailto:bbmaster@bbtech.co.kr" style={{ cursor: 'pointer' }}>
          <TitleNormal fontSize={12} color='white'>{t('bbmaster@bbtech.co.kr')}</TitleNormal>
        </a>
        <a href="tel:03180090777" style={{ cursor: 'pointer' }}>
          <TitleNormal fontSize={12} color='white'>{t('032-8009-0777')}</TitleNormal>
        </a>
      </HorizontalEqualLayout>
      <BTBox height={25} />
      <HorizontalLayout wrapContents={true} style={{ alignItems: 'center' }}>
        <TitleNormalBold
          fontSize={13}
          color="white"
          style={{ fontWeight: '700', cursor: 'pointer' }}
          onClick={() => {
            window.open('https://www.bbtechamerica.com/', '_blank');
          }}
        >
          {t('비비테크 아메리카')}
        </TitleNormalBold>
        <BTBox width={6} />
        <img src={ArrowRightGrey} />
      </HorizontalLayout>
      <BTBox height={25} />
      <TitleNormal
        fontSize={13}
        color="white"
        style={{ opacity: 0.4 }}
      >
        {copyright?.copyright}
      </TitleNormal>
      <BTBox height={38} />
      <HorizontalLayout>
        <img src={WebAwardLogo} />
        <BTBox width={23} />
        <VerticalLayout>
          <BTBox height={12} />
          <TitleNormal color='white' fontSize={pxToRem(14)} lineHeight='18px'>
            <Trans i18nKey='2025 웹어워드 코리아 최우수상 수상' />
          </TitleNormal>
        </VerticalLayout>
      </HorizontalLayout>
    </VerticalLayout>
  );
};

export default FooterSectionMobile;

